/**
 * QuoteViewerModal ✦ iter226
 *
 * The shared quote surface — used by /admin/bookings (quick
 * action) and /admin/quotes (archive). Visually mirrors the PDF
 * so Craig sees exactly what the customer will receive.
 *
 * Three modes:
 *   1. Read-only header (quote number, status pill, dates)
 *   2. Editable line items + travel + deposit + terms
 *   3. Actions: Save · Download PDF · Email Quote · Mark Deposit Received
 *
 * Lifecycle hooks (status pill):
 *   draft → sent (via Email Quote)
 *   sent → accepted (via Mark Deposit Received → also flips
 *                    linked booking to confirmed)
 *   declined / expired (admin-driven via the status dropdown)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const GOLD_HAIRLINE = 'rgba(212,170,82,0.14)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const QUOTE_STATUSES = [
  { key: 'draft',    label: 'Draft',    tint: 'rgba(226,199,123,0.30)', text: '#F4E2A8' },
  { key: 'sent',     label: 'Sent',     tint: 'rgba(212,170,82,0.30)',  text: '#F0DCA8' },
  { key: 'accepted', label: 'Accepted', tint: 'rgba(125,181,140,0.42)', text: '#CFEFC6' },
  { key: 'declined', label: 'Declined', tint: 'rgba(184,111,111,0.32)', text: '#E6B5B5' },
  { key: 'expired',  label: 'Expired',  tint: 'rgba(140,110,110,0.30)', text: '#D1A8A8' },
];
const STATUS_MAP = QUOTE_STATUSES.reduce((a, s) => { a[s.key] = s; return a; }, {});

const inputBase = {
  width: '100%',
  background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 8, color: IVORY, fontFamily: SERIF, fontSize: 13.5,
  padding: '9px 12px', outline: 'none',
};
const labelStyle = {
  display: 'block', marginBottom: 5,
  fontFamily: SERIF, fontStyle: 'italic', fontSize: 10.5,
  letterSpacing: '0.18em', textTransform: 'uppercase', color: IVORY_GHOST,
};
const primaryPillStyle = {
  padding: '9px 18px', borderRadius: 999,
  border: `1px solid ${GOLD}`,
  background: 'linear-gradient(135deg, rgba(212,170,82,0.22), rgba(184,151,98,0.12))',
  color: IVORY, fontFamily: SERIF, fontSize: 13, letterSpacing: '0.04em', cursor: 'pointer',
};
const ghostPillStyle = {
  padding: '9px 18px', borderRadius: 999,
  background: 'transparent', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 13, cursor: 'pointer',
};

const QuoteViewerModal = ({ quoteId, onClose, onChange }) => {
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!quoteId) return;
    axios.get(`${API}/admin/quotes/${quoteId}`).then((r) => setQuote(r.data.quote))
      .catch(() => toast.error('Could not load quote.'));
  }, [quoteId]);

  if (!quote) {
    return (
      <Backdrop onClose={onClose}>
        <div style={modalShellStyle}>
          <p style={{ color: IVORY_DIM, fontStyle: 'italic', textAlign: 'center' }}>Loading quote ✦</p>
        </div>
      </Backdrop>
    );
  }

  const upd = (k, v) => setQuote((q) => ({ ...q, [k]: v }));
  const updItem = (idx, patch) => setQuote((q) => ({
    ...q,
    line_items: q.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li),
  }));
  const addItem = (kind = 'custom') => setQuote((q) => ({
    ...q,
    line_items: [...q.line_items, { kind, slug: '', description: '', qty: 1, unit_price_pounds: 0, line_total_pounds: 0 }],
  }));
  const removeItem = (idx) => setQuote((q) => ({
    ...q,
    line_items: q.line_items.filter((_, i) => i !== idx),
  }));

  const save = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      const payload = {
        customer_name: quote.customer_name,
        customer_email: quote.customer_email,
        customer_phone: quote.customer_phone,
        event_date: quote.event_date,
        venue: quote.venue,
        event_type: quote.event_type,
        line_items: quote.line_items.map((li) => ({
          kind: li.kind, slug: li.slug || '', description: li.description,
          qty: Number(li.qty) || 0,
          unit_price_pounds: Number(li.unit_price_pounds) || 0,
        })),
        travel_miles: Number(quote.travel_miles) || 0,
        travel_charge_pounds: Number(quote.travel_charge_pounds) || 0,
        deposit_required_pounds: Number(quote.deposit_required_pounds) || 0,
        terms: quote.terms,
        notes: quote.notes,
        status: quote.status,
        valid_until_date: quote.valid_until_date,
      };
      const r = await axios.patch(`${API}/admin/quotes/${quote.quote_id}`, payload);
      setQuote(r.data.quote);
      if (!silent) toast.success('Quote saved ✦');
      onChange?.();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = () => {
    // iter238d — cache-buster so the browser never serves a
    // stale PDF from a previous click.
    window.open(`${API}/admin/quotes/${quote.quote_id}/pdf?t=${Date.now()}`, '_blank');
  };

  const emailQuote = async () => {
    if (!window.confirm(`Email this quote to ${quote.customer_email}?`)) return;
    try {
      const r = await axios.post(`${API}/admin/quotes/${quote.quote_id}/email`);
      setQuote(r.data.quote);
      onChange?.();
      if (r.data.email_sent) toast.success('Quote emailed ✦');
      else toast.warning('Status updated to Sent — but the email did not deliver. Please send manually.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed.');
    }
  };

  const markDepositReceived = async () => {
    if (!window.confirm('Mark deposit received? Linked booking will be set to Confirmed and inventory will deduct.')) return;
    try {
      const r = await axios.post(`${API}/admin/quotes/${quote.quote_id}/mark-deposit-received`);
      setQuote(r.data.quote);
      onChange?.();
      toast.success(r.data.booking_confirmed
        ? 'Deposit received ✦ Booking Confirmed.'
        : 'Quote marked Accepted ✦');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    }
  };

  // iter238 — Convert this quote into a proper business Invoice
  // (own numbering, full business identity block, mark-paid flow).
  // Idempotent: pressing twice returns the same invoice, and if a
  // draft invoice already exists the backend refreshes its line
  // items from the current quote so operator edits carry through.
  const _ensureInvoice = async () => {
    // iter238b — persist any pending edits first so the invoice is
    // built from what the operator sees on screen, not the stale
    // DB copy. Silent save avoids double-toasting.
    const saved = await save({ silent: true });
    if (!saved) throw new Error('Could not save pending quote edits.');
    const r = await axios.post(
      `${API}/admin/quotes/${quote.quote_id}/convert-to-invoice`,
    );
    return { invoice: r.data.invoice, created: r.data.created,
             refreshed: r.data.refreshed };
  };

  const previewInvoice = async () => {
    try {
      // iter238d — compute what we EXPECT the invoice subtotal to
      // be, based on the on-screen quote edits. After conversion
      // we compare this against the actual invoice — mismatch
      // means the backend didn't refresh (likely stale deploy or
      // paid-invoice lock) and we surface a loud toast so the
      // operator never emails a wrong figure.
      const expectedSubtotal = (quote.line_items || []).reduce(
        (s, li) => s + (Math.max(0, Number(li.qty) || 0)
                        * Math.max(0, Number(li.unit_price_pounds) || 0)),
        0,
      );
      const { invoice, created, refreshed } = await _ensureInvoice();
      if (created) toast.success(`Invoice ${invoice.invoice_number} created ✦`);
      else if (refreshed) toast.info(`Invoice ${invoice.invoice_number} refreshed to £${invoice.subtotal_pounds} ✦`);
      if (Number(invoice.subtotal_pounds) !== expectedSubtotal) {
        toast.error(
          `⚠ Invoice shows £${invoice.subtotal_pounds} but the quote is `
          + `£${expectedSubtotal}. The backend didn't refresh — either `
          + `the invoice is already Paid (locked), or the latest fix `
          + `isn't deployed. Redeploy the app and try again.`,
          { duration: 12000 },
        );
      }
      window.open(
        `${API}/admin/invoices/${invoice.invoice_id}/pdf?t=${Date.now()}`,
        '_blank',
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Preview invoice failed.');
    }
  };

  const sendInvoice = async () => {
    const msg = `Send a business invoice to ${quote.customer_email}? `
      + `It will carry your full business details (address, phone) and `
      + `bank details for payment.`;
    if (!window.confirm(msg)) return;
    try {
      const expectedSubtotal = (quote.line_items || []).reduce(
        (s, li) => s + (Math.max(0, Number(li.qty) || 0)
                        * Math.max(0, Number(li.unit_price_pounds) || 0)),
        0,
      );
      const { invoice, refreshed } = await _ensureInvoice();
      if (Number(invoice.subtotal_pounds) !== expectedSubtotal) {
        toast.error(
          `⚠ Blocked: invoice would send at £${invoice.subtotal_pounds} `
          + `but your quote is £${expectedSubtotal}. Fix the invoice `
          + `first (redeploy or cancel & re-issue) before sending.`,
          { duration: 15000 },
        );
        return;  // Do NOT dispatch the email on a mismatched figure.
      }
      if (refreshed) toast.info(`Invoice refreshed to £${invoice.subtotal_pounds} ✦`);
      const r = await axios.post(
        `${API}/admin/invoices/${invoice.invoice_id}/email`,
      );
      if (r.data.email_sent) {
        toast.success(`Invoice ${invoice.invoice_number} emailed to ${quote.customer_email} ✦`);
      } else {
        toast.warning(`Invoice ${invoice.invoice_number} marked as Sent — `
          + 'but delivery failed. Please send manually.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Send invoice failed.');
    }
  };

  const logContact = async () => {
    try {
      const r = await axios.post(`${API}/admin/quotes/${quote.quote_id}/log-contact`);
      setQuote(r.data.quote);
      onChange?.();
      toast.success('Contact logged ✦');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Log contact failed.');
    }
  };

  const statusInfo = STATUS_MAP[quote.status] || STATUS_MAP.draft;

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="quote-viewer-modal" style={modalShellStyle}>
        {/* Quote header */}
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14, marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 10.5, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: GOLD, marginBottom: 6 }}>
              ✦ EventDrop · Quote
            </div>
            <h2 data-testid="quote-number" style={{
              fontFamily: SERIF, fontWeight: 500, fontSize: 24, margin: 0, letterSpacing: '-0.005em',
            }}>{quote.quote_number}</h2>
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, color: IVORY_DIM, marginTop: 6 }}>
              Issued {quote.issue_date} · Valid until {quote.valid_until_date}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <select
              value={quote.status}
              onChange={(e) => upd('status', e.target.value)}
              data-testid="quote-status-select"
              style={{
                padding: '6px 12px', borderRadius: 999,
                background: statusInfo.tint, color: statusInfo.text,
                border: `1px solid ${statusInfo.tint}`,
                fontFamily: SERIF, fontSize: 12, fontStyle: 'italic', cursor: 'pointer',
                letterSpacing: '0.04em',
              }}>
              {QUOTE_STATUSES.map(s => (
                <option key={s.key} value={s.key} style={{ background: '#0F0B1A', color: IVORY }}>{s.label}</option>
              ))}
            </select>
            {quote.booking_id && (
              <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, color: IVORY_GHOST }}>
                Linked to booking {quote.booking_id.slice(0, 10)}…
              </div>
            )}
          </div>
        </header>

        {/* Customer + event */}
        <section style={twoColStyle}>
          <div>
            <div style={labelStyle}>Quoted for</div>
            <input value={quote.customer_name || ''} onChange={(e) => upd('customer_name', e.target.value)}
              style={{ ...inputBase, fontSize: 15, fontWeight: 500 }} data-testid="qv-customer-name" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <input type="email" value={quote.customer_email || ''} onChange={(e) => upd('customer_email', e.target.value)}
                placeholder="Email" style={inputBase} data-testid="qv-customer-email" />
              <input value={quote.customer_phone || ''} onChange={(e) => upd('customer_phone', e.target.value)}
                placeholder="Phone" style={inputBase} data-testid="qv-customer-phone" />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Event</div>
            <input type="date" value={quote.event_date || ''} onChange={(e) => upd('event_date', e.target.value)}
              style={{ ...inputBase, colorScheme: 'dark', fontSize: 15, fontWeight: 500 }} data-testid="qv-event-date" />
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10, marginTop: 10 }}>
              <input value={quote.venue || ''} onChange={(e) => upd('venue', e.target.value)}
                placeholder="Venue" style={inputBase} data-testid="qv-venue" />
              <input value={quote.event_type || ''} onChange={(e) => upd('event_type', e.target.value)}
                placeholder="Event type" style={inputBase} data-testid="qv-event-type" />
            </div>
          </div>
        </section>

        {/* Line items */}
        <section style={{ marginTop: 22 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <div style={labelStyle}>Line items</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => addItem('addon')}
                data-testid="qv-add-addon"
                style={{ ...ghostPillStyle, padding: '4px 12px', fontSize: 11.5 }}>+ Add-on</button>
              <button type="button" onClick={() => addItem('equipment')}
                data-testid="qv-add-equipment"
                style={{ ...ghostPillStyle, padding: '4px 12px', fontSize: 11.5 }}>+ Equipment</button>
              <button type="button" onClick={() => addItem('custom')}
                data-testid="qv-add-custom"
                style={{ ...ghostPillStyle, padding: '4px 12px', fontSize: 11.5 }}>+ Custom</button>
            </div>
          </div>
          <table data-testid="quote-line-items" style={{ width: '100%', borderCollapse: 'collapse', fontFamily: SERIF }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={thStyle}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 70 }}>Qty</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 110 }}>Unit £</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 110 }}>Total £</th>
                <th style={{ ...thStyle, width: 36 }} aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {(quote.line_items || []).map((li, idx) => {
                const qty = Number(li.qty) || 0;
                const unit = Number(li.unit_price_pounds) || 0;
                const total = qty * unit;
                return (
                  <tr key={idx} data-testid={`quote-li-row-${idx}`}>
                    <td style={tdStyle}>
                      <input value={li.description || ''}
                        onChange={(e) => updItem(idx, { description: e.target.value })}
                        style={{ ...inputBase, padding: '6px 10px' }} />
                      <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 10.5,
                        color: IVORY_GHOST, marginTop: 2, display: 'block', letterSpacing: '0.06em' }}>
                        {li.kind}{li.slug ? ` · ${li.slug}` : ''}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <input type="number" min="0" max="99" value={qty}
                        onChange={(e) => updItem(idx, { qty: Number(e.target.value) })}
                        style={{ ...inputBase, padding: '6px 10px', textAlign: 'right' }} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <input type="number" min="0" max="99999" value={unit}
                        onChange={(e) => updItem(idx, { unit_price_pounds: Number(e.target.value) })}
                        style={{ ...inputBase, padding: '6px 10px', textAlign: 'right' }} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: SERIF, fontSize: 14, color: IVORY }}>
                      £{total}
                    </td>
                    <td style={tdStyle}>
                      <button type="button" onClick={() => removeItem(idx)}
                        title="Remove"
                        style={{ background: 'transparent', border: 'none', color: 'rgba(184,111,111,0.78)',
                          fontFamily: SERIF, fontSize: 16, cursor: 'pointer' }}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {/* Travel + totals */}
        <section style={{
          marginTop: 22, padding: '20px 20px',
          background: 'rgba(212,170,82,0.04)',
          border: `1px dashed ${GOLD_FAINT}`,
          borderRadius: 12,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Travel miles (from Wrexham)</label>
              <input type="number" min="0" max="9999"
                value={quote.travel_miles ?? 0}
                onChange={(e) => upd('travel_miles', Number(e.target.value))}
                style={inputBase} data-testid="qv-travel-miles" />
              <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, color: IVORY_GHOST, marginTop: 4 }}>
                Band: {quote.travel_band || '0-50'}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Travel charge override (£)</label>
              <input type="number" min="0" max="9999"
                value={quote.travel_charge_pounds ?? 0}
                onChange={(e) => upd('travel_charge_pounds', Number(e.target.value))}
                style={inputBase} data-testid="qv-travel-charge" />
            </div>
            <div>
              <label style={labelStyle}>Deposit required (£)</label>
              <input type="number" min="0" max="99999"
                value={quote.deposit_required_pounds ?? 0}
                onChange={(e) => upd('deposit_required_pounds', Number(e.target.value))}
                style={inputBase} data-testid="qv-deposit-required" />
            </div>
            <div>
              <label style={labelStyle}>Valid until</label>
              <input type="date" value={quote.valid_until_date || ''}
                onChange={(e) => upd('valid_until_date', e.target.value)}
                style={{ ...inputBase, colorScheme: 'dark' }} data-testid="qv-valid-until" />
            </div>
          </div>
          <div style={totalsBoxStyle}>
            {/* iter234a — totals live-derive from current form state
              * so editing the deposit/line-items immediately reflects
              * the new balance, without waiting for Save to round-
              * trip to the backend. */}
            {(() => {
              const liveSubtotal = (quote.line_items || []).reduce(
                (s, li) => s + (Math.max(0, Number(li.qty) || 0) * Math.max(0, Number(li.unit_price_pounds) || 0)),
                0,
              );
              const liveDeposit = Math.max(0, Number(quote.deposit_required_pounds) || 0);
              const liveBalance = Math.max(0, liveSubtotal - liveDeposit);
              return (
                <>
                  <TotalsRow label="Subtotal" value={liveSubtotal} testid="qv-subtotal" />
                  <TotalsRow label="Deposit required" value={liveDeposit} bold testid="qv-deposit-line" />
                  <TotalsRow label="Balance remaining" value={liveBalance} testid="qv-balance" />
                </>
              );
            })()}
          </div>
        </section>

        {/* Terms */}
        <section style={{ marginTop: 22 }}>
          <label style={labelStyle}>Terms (printed on PDF)</label>
          <textarea value={quote.terms || ''} onChange={(e) => upd('terms', e.target.value)}
            rows={4} style={{ ...inputBase, resize: 'vertical', minHeight: 80 }} data-testid="qv-terms" />
          <label style={{ ...labelStyle, marginTop: 12 }}>Internal notes (admin only)</label>
          <textarea value={quote.notes || ''} onChange={(e) => upd('notes', e.target.value)}
            rows={3} style={{ ...inputBase, resize: 'vertical', minHeight: 60 }} data-testid="qv-notes" />
        </section>

        {/* Actions */}
        <footer style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end', marginTop: 26 }}>
          <button type="button" onClick={onClose} style={ghostPillStyle}>Close</button>
          <button type="button" onClick={downloadPdf} data-testid="qv-download-pdf"
            style={{ ...ghostPillStyle, color: IVORY, borderColor: GOLD_HAIRLINE }}>
            Download PDF
          </button>
          <button type="button" onClick={emailQuote} data-testid="qv-email-quote"
            style={{ ...ghostPillStyle, color: IVORY, borderColor: GOLD_HAIRLINE }}>
            Email Quote ✦
          </button>
          <button type="button" onClick={previewInvoice} data-testid="qv-preview-invoice"
            style={{ ...ghostPillStyle, color: IVORY, borderColor: GOLD_HAIRLINE }}>
            Preview Invoice ✦
          </button>
          <button type="button" onClick={sendInvoice} data-testid="qv-send-invoice"
            style={{ ...ghostPillStyle, color: IVORY, borderColor: GOLD_HAIRLINE }}>
            Send Invoice ✦
          </button>
          <button type="button" onClick={logContact} data-testid="qv-log-contact"
            style={{ ...ghostPillStyle, color: IVORY, borderColor: GOLD_HAIRLINE }}>
            {quote.last_contact_date
              ? `Re-log contact (${quote.last_contact_date})`
              : 'Log contact ✦'}
          </button>
          {quote.booking_id && (
            <button type="button" onClick={markDepositReceived} data-testid="qv-mark-deposit-received"
              style={{ ...primaryPillStyle, borderColor: GOLD }}>
              Mark Deposit Received ✦
            </button>
          )}
          <button type="button" onClick={save} disabled={saving} data-testid="qv-save"
            style={{ ...primaryPillStyle, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </div>
    </Backdrop>
  );
};

const Backdrop = ({ children, onClose }) => (
  <div
    onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    style={{
      position: 'fixed', inset: 0, zIndex: 110,
      background: 'rgba(8,5,3,0.78)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: 'clamp(20px, 4vw, 50px) clamp(16px, 4vw, 40px)',
      overflowY: 'auto',
    }}>
    {children}
  </div>
);

const modalShellStyle = {
  width: '100%', maxWidth: 880,
  background: '#1A1325',
  border: `1px solid ${GOLD_FAINT}`,
  borderRadius: 14,
  padding: 'clamp(22px, 4vw, 36px)',
  color: IVORY,
};

const twoColStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 18,
};

const thStyle = {
  padding: '8px 8px',
  borderBottom: `1px solid ${GOLD_FAINT}`,
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
  color: IVORY_DIM, fontWeight: 500,
};
const tdStyle = {
  padding: '8px 8px',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  fontFamily: SERIF, fontSize: 13.5, color: IVORY,
  verticalAlign: 'top',
};
const totalsBoxStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 6, paddingTop: 12,
  borderTop: `1px solid ${GOLD_HAIRLINE}`,
};

const TotalsRow = ({ label, value, bold, testid }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between',
    fontFamily: SERIF,
    fontSize: bold ? 16 : 14, color: IVORY,
    fontWeight: bold ? 600 : 400,
    padding: '4px 0',
  }}>
    <span style={{ color: IVORY_DIM, fontStyle: 'italic' }}>{label}</span>
    <span data-testid={testid}>£{(value || 0).toLocaleString('en-GB')}</span>
  </div>
);

export default QuoteViewerModal;
